import { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import CsvDataTable from "../../spatial-datasets/CsvDataTable";

interface TableRendererProps {
  /** Raw CSV S3 key the `/api/v2/csv-data/` endpoint is keyed by. */
  fileKey: string | null | undefined;
  filename?: string;
  columns?: string[];
  mode?: "preview" | "thumbnail" | "dialog" | "expanded";
  width?: string | number;
  height?: string | number;
  onLoad?: () => void;
  onError?: () => void;
  onClick?: () => void;
}

/**
 * MediaViewer renderer that shows a tabular (CSV) media item as an inline, read-only,
 * paginated table — reusing {@link CsvDataTable}. This is what "exposing the viewer as a
 * MediaViewer mode" means: the same browsable table renders wherever MediaViewer is used
 * (media drawer, item details, carousels), not just from the dataset card dialog.
 *
 * The table is driven by the CSV's `file_key` (via the config-driven csv-data endpoint),
 * so it only activates when the media item carries one. Items without a file key fall
 * back to the standard document/download renderer upstream in MediaViewer.
 */
export default function TableRenderer({
  fileKey,
  filename,
  columns,
  mode = "preview",
  width = "100%",
  height,
  onLoad,
  onClick
}: TableRendererProps) {
  // The table manages its own fetching/loading; signal "loaded" on mount so MediaViewer's
  // parent loading overlays clear (this renderer never uses the URL pipeline).
  useEffect(() => {
    onLoad?.();
  }, [onLoad]);

  const isThumbnail = mode === "thumbnail";

  if (isThumbnail) {
    // Thumbnails/tiles: don't fetch rows — just show a lightweight table affordance.
    return (
      <Box
        onClick={onClick}
        sx={{
          width,
          height: height ?? "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: onClick ? "pointer" : "default",
          bgcolor: "grey.100"
        }}
      >
        <Typography variant='caption' color='text.secondary'>
          {filename || "Table data"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width, height: height ?? "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <CsvDataTable fileKey={fileKey} columns={columns} maxHeight={mode === "expanded" ? "75vh" : "55vh"} />
    </Box>
  );
}
